import { AfterViewInit, Component, ContentChildren, ElementRef, Input, NgModule, OnInit, Output, QueryList, ViewChild, ViewEncapsulation } from '@angular/core';

import { CommonModule } from '@angular/common';
import { FacetLinksDirective } from './facet-links.directive';
import { RouterModule } from '@angular/router';
import { WButtonGroupModule } from '@wipo/w-angular/w-button-group';
import { WStickyBarLinkComponent } from './w-sticky-bar-link.component';
import { IconDirectiveModule, WClickOutsideModule, WHideEmptyModule } from '@wipo/w-angular/directives';
import { BehaviorSubject, fromEvent, Observable } from 'rxjs';
import { WUniversalModule, document, window, isBrowser, WRxBase, maxZIndex } from '@wipo/w-angular/shared';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { WInputSearchComponent } from '@wipo/w-angular/w-input-search';

@Component({
    selector: 'w-sticky-bar',
    templateUrl: './w-sticky-bar.component.html',
    styleUrls: ['./w-sticky-bar.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'b-sticky-bar b-navigation--has-close',
        '[class.b-navigation--style-1]': 'version === 2',
        '[class.b-navigation--theme-dark]': 'theme === "dark"',
        '[class.b-navigation--theme-light]': 'theme === "light"',
        '[style.height]': 'height'
    },
    standalone: false
})
/*
@id w-sticky-bar
@name w-sticky-bar
@status release
*/
export class WStickyBarComponent extends WRxBase implements OnInit, AfterViewInit {
  @ViewChild('floater') floater?: ElementRef<HTMLDivElement>;
  @ViewChild('spacer') spacer!: ElementRef<HTMLDivElement>;

  @ContentChildren(WInputSearchComponent) searchComponents?: QueryList<WInputSearchComponent>;

  get hasSearch() {
    return this.searchComponents && this.searchComponents.length > 0;
  }
  height: string = '60px';
  bgColor?: string;
  dir?: string;
  @Input() sticky: boolean = false; // floating at top
  @Input() landingClass?: string;  // provide class for landing page bar, @options[landing, landing dark, <empty string>]
  @Input() version = 1; // 1 - original (default), 2 - October 2024 (blue with white background + mobile mode)
  @Input() theme = 'light'; // @no-doc light or dark
  _toggleMode = new BehaviorSubject<boolean>(false);
  @Output() toggleCompactMode: Observable<boolean> = this._toggleMode.pipe(takeUntil(this.destruction), distinctUntilChanged()); // emits when mode toggled, compact mode (true)

  width = new BehaviorSubject(`${document.documentElement.clientWidth}`);
  /** mobile */
  compactTreshold = 0;
  compactMode: boolean = false; // compact mode for mobile
  open = false;
  maxZIndex = 0;
  hasSelected = false;

  resizeObserver = new ResizeObserver(entries => {
    for (let entry of entries) {
      const { width } = entry.contentRect;
      this.detectSelection();
      if (width === 0) {
        this.resizeObserver.unobserve(entry.target);
        this._toggleMode.next(true);
        this.compactTreshold = document.documentElement.clientWidth + 50; // at this point we will try build desktop version again
        const s = fromEvent(window, 'resize').pipe(takeUntil(this.destruction)).subscribe(() => {
          if (document.documentElement.clientWidth > this.compactTreshold) {
            this._toggleMode.next(false);
            const handle = setInterval(() => {
              /** start again observing spacer in desktop mode */
              if (this.spacer) {
                this.resizeObserver.observe(this.spacer.nativeElement);
                s.unsubscribe();
                clearInterval(handle);
              }
            }, 50); // re-observe
          }
        });
      } else {
        this._toggleMode.next(false);
      }
    }
  });
  constructor(private elRef: ElementRef) {
    super();
  }
  ngOnInit(): void {
    this.toggleCompactMode.subscribe(compact => {
      if (!compact) this.width.next('auto');
      this.compactMode = compact;
    });
    const calc = this.calcHeight.bind(this);
    this.fromEvent(document, 'scroll').subscribe(calc);
    this.fromEvent(window, 'resize').subscribe(calc);
    this.width.pipe(takeUntil(this.destruction), distinctUntilChanged()).subscribe(calc);
    setTimeout(calc, 100); // first measurement
  }
  ngAfterViewInit(): void {
    if (this.version > 1) {
      this.detectSelection();
      this.resizeObserver.observe(this.spacer.nativeElement);
    }
  }
  /** detect if we have a tab selected, also make sure we know the max zIndex on page */
  private detectSelection() {
    this.hasSelected = this.elRef.nativeElement.querySelector('.b-navigation__shortcut-item--is-selected') !== null;
    this.maxZIndex = maxZIndex();
  }
  calcHeight() {
    const floater = this.floater;
    if (isBrowser && floater) {
      setTimeout(() => {
        this.height = this.compactMode ? 'auto' : `${floater.nativeElement.clientHeight}px`;
        this.bgColor = getComputedStyle(floater.nativeElement).backgroundColor;
        this.dir = getComputedStyle(floater.nativeElement).direction;
        const w = floater.nativeElement.clientWidth - (this.sticky ? 2 * floater.nativeElement.offsetLeft : 0) * (this.dir === 'rtl' ? -1 : 1);
        this.width.next(`${w}px`);
      });
    }
  }
  linkOrToggle(event: Event) {
    const target = event.target as HTMLElement;
    if (target.tagName.toLowerCase() !== 'a') {
      this.open = !this.open;
    } else {
      /** close delayed */
      setTimeout(() => {
        this.detectSelection();
        this.open = false;
      });
    }
  }
}
@NgModule({
  imports: [CommonModule, WButtonGroupModule, RouterModule, WHideEmptyModule, WUniversalModule, IconDirectiveModule, WClickOutsideModule],
  exports: [WStickyBarComponent, WStickyBarLinkComponent, FacetLinksDirective],
  declarations: [WStickyBarComponent, WStickyBarLinkComponent, FacetLinksDirective]
})
export class WStickyBarModule { }
